import * as Sentry from '@sentry/react';

export const unifiedModuleColors = {
  white: 'white',
  grey: 'grey',
  lightBlue: 'light-blue',
  mistyBlue: 'misty-blue',
  blue: 'blue',
  darkBlue: 'dark-blue',
  black: 'black',
  red: 'red',
  darkRed: 'dark-red',
  lightPurple: 'light-purple',
  lightYellow: 'light-yellow',
  lightGreen: 'light-green',
  quaternaryPurpleXWeak: 'quaternary-purple-xweak',
  primaryBlueXWeak: 'primary-blue-xweak',
  primaryBlueWeak: 'primary-blue-weak',
  primaryBlueStrong: 'primary-blue-strong',
  primaryBlueXStrong: 'primary-blue-xstrong',
  primaryBlueXXStrong: 'primary-blue-xxstrong',
  neutralGreyXWeak: 'neutral-grey-xweak',
  neutralGreyWeak: 'neutral-grey-weak',
  neutralGreyXStrong: 'neutral-grey-xstrong',
  neutralGreyXXStrong: 'neutral-grey-xxstrong',
  neutralPrimary: 'neutral-primary',
  tertiaryYellowXWeak: 'tertiary-yellow-xweak',
  tertiaryYellowWeak: 'tertiary-yellow-weak',
  // Logged-in home spotlight panels.
  experimentalColorBluePurpleAbstractGradient: 'experimental-color-blue-purple-abstract-gradient',
  experimentalColorBlueVioletAbstractGradient: 'experimental-color-blue-violet-abstract-gradient',
  // the below colors are for the GrowthDiscovery.promoBarBannerColourExperiment. Please do not use them for other purposes.
  experimentalColorBlueAbstractGradient: 'experimental-color-blue-abstract-gradient',
  experimentalColorPinkAbstractGradient: 'experimental-color-pink-abstract-gradient',
  experimentalColorExpressiveYellow: 'experimental-color-expressive-yellow',
};

const COLOR_MAP: Record<string, string> = {
  [unifiedModuleColors.white]: 'var(--cds-color-neutral-background-primary)',
  [unifiedModuleColors.grey]: 'var(--cds-color-neutral-background-primary-weak)',
  [unifiedModuleColors.lightBlue]: 'var(--cds-color-emphasis-primary-background-xweak)',
  [unifiedModuleColors.mistyBlue]: 'var(--cds-color-emphasis-primary-background-weak)',
  [unifiedModuleColors.blue]: 'var(--cds-color-emphasis-primary-background-strong)',
  [unifiedModuleColors.darkBlue]: 'var(--cds-color-emphasis-primary-background-xxstrong)',
  [unifiedModuleColors.black]: 'var(--cds-color-neutral-primary)',
  [unifiedModuleColors.red]: 'var(--cds-color-feedback-error)',
  [unifiedModuleColors.darkRed]: 'var(--cds-color-red-800)',
  [unifiedModuleColors.lightPurple]: `var(--cds-color-callouts-background-secondary-weak)`,
  [unifiedModuleColors.lightYellow]: `var(--cds-color-callouts-background-tertiary-weak)`,
  [unifiedModuleColors.lightGreen]: `var(--cds-color-feedback-background-success-weak)`,
  [unifiedModuleColors.quaternaryPurpleXWeak]: `var(--cds-color-emphasis-quaternary-background-xweak)`,
  [unifiedModuleColors.primaryBlueXWeak]: `var(--cds-color-emphasis-primary-background-xweak)`,
  [unifiedModuleColors.primaryBlueWeak]: `var(--cds-color-emphasis-primary-background-weak)`,
  [unifiedModuleColors.primaryBlueStrong]: `var(--cds-color-emphasis-primary-background-strong)`,
  [unifiedModuleColors.primaryBlueXStrong]: `var(--cds-color-emphasis-primary-background-xstrong)`,
  [unifiedModuleColors.primaryBlueXXStrong]: `var(--cds-color-emphasis-primary-background-xxstrong)`,
  [unifiedModuleColors.neutralGreyWeak]: `var(--cds-color-emphasis-neutral-background-weak)`,
  [unifiedModuleColors.neutralGreyXWeak]: `var(--cds-color-emphasis-neutral-background-xweak)`,
  [unifiedModuleColors.neutralGreyXStrong]: `var(--cds-color-emphasis-neutral-background-xstrong)`,
  [unifiedModuleColors.neutralGreyXXStrong]: `var(--cds-color-emphasis-neutral-background-xxstrong)`,
  [unifiedModuleColors.neutralPrimary]: `var(--cds-color-neutral-background-primary)`,
  [unifiedModuleColors.tertiaryYellowXWeak]: `var(--cds-color-emphasis-tertiary-background-xweak)`,
  [unifiedModuleColors.tertiaryYellowWeak]: `var(--cds-color-emphasis-tertiary-background-weak)`,
  [unifiedModuleColors.experimentalColorExpressiveYellow]: `var(--cds-color-yellow-50)`,
  [unifiedModuleColors.experimentalColorBlueAbstractGradient]: `linear-gradient(87deg, #3587FC 2.18%, #0048B0 97.23%)`,
  [unifiedModuleColors.experimentalColorPinkAbstractGradient]: `linear-gradient(90deg, #C429A8 0.03%, #3587FC 99.95%)`,
  [unifiedModuleColors.experimentalColorBluePurpleAbstractGradient]: `linear-gradient(90deg, #136EF0 0%, #3C0F8A 100%)`,
  [unifiedModuleColors.experimentalColorBlueVioletAbstractGradient]: `linear-gradient(90deg, #005CC5 0%, #9747FF 100%)`,
};

export type TextColorOverride = 'light' | 'dark' | 'auto' | null;

export function parseTextColorOverride(value?: string | null): TextColorOverride {
  if (value === 'light' || value === 'dark' || value === 'auto') return value;
  return null;
}

export function isGradient(value: string): boolean {
  return value.includes('linear-gradient(') || value.includes('radial-gradient(');
}

/**
 * Uses the same regex as Contentful's validation for hex values in customBackgroundColor.
 */
function isValidHexColor(color: string): boolean {
  return /^#([0-9a-fA-F]{3}){1,2}$/.test(color);
}

const DARK_BACKGROUND_LUMINANCE_THRESHOLD = 0.179;

/**
 * WCAG 2.0 relative luminance calculation for hex colors.
 * Returns a value between 0 (black) and 1 (white).
 */
function hexToRelativeLuminance(hex: string): number {
  const cleaned = hex.replace('#', '');

  if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(cleaned)) {
    return 0.5;
  }

  let r: number;
  let g: number;
  let b: number;

  if (cleaned.length === 3) {
    r = parseInt(cleaned.charAt(0) + cleaned.charAt(0), 16) / 255;
    g = parseInt(cleaned.charAt(1) + cleaned.charAt(1), 16) / 255;
    b = parseInt(cleaned.charAt(2) + cleaned.charAt(2), 16) / 255;
  } else {
    r = parseInt(cleaned.slice(0, 2), 16) / 255;
    g = parseInt(cleaned.slice(2, 4), 16) / 255;
    b = parseInt(cleaned.slice(4, 6), 16) / 255;
  }

  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

export function getColorFromColorName(colorName?: string | null) {
  if (!colorName || colorName === 'none') {
    return null;
  }

  const mapped = COLOR_MAP[colorName];
  if (mapped) return mapped;

  if (isValidHexColor(colorName) || isGradient(colorName)) {
    return colorName;
  }

  return null;
}

const INVERTED_NAMED_COLORS = [
  'black',
  'dark-blue',
  'blue',
  'red',
  'dark-red',
  'neutral-grey-xstrong',
  'neutral-grey-xxstrong',
  'primary-blue-strong',
  'primary-blue-xstrong',
  'primary-blue-xxstrong',
  'experimental-color-pink-abstract-gradient',
  'experimental-color-blue-abstract-gradient',
  'experimental-color-blue-purple-abstract-gradient',
  'experimental-color-blue-violet-abstract-gradient',
];

export function isInvertedColor(colorName: string) {
  if (INVERTED_NAMED_COLORS.includes(colorName)) return true;
  if (isGradient(colorName)) return true;
  if (isValidHexColor(colorName)) {
    return hexToRelativeLuminance(colorName) < DARK_BACKGROUND_LUMINANCE_THRESHOLD;
  }
  return false;
}

export function getFgColorFromBgColorName(colorName: string) {
  if (isInvertedColor(colorName)) {
    return 'var(--cds-color-neutral-primary-invert)';
  } else {
    return 'var(--cds-color-neutral-primary)';
  }
}

export const TYPOGRAPHY_LIGHT_COLOR = 'var(--cds-color-neutral-primary-invert)';
export const TYPOGRAPHY_DARK_COLOR = 'var(--cds-color-neutral-primary)';

/**
 * Parses a custom background value (hex or gradient) into the correct CSS property/value pair.
 */
export function getCustomBackgroundStyle(
  value?: string | null
): { property: 'background-image' | 'background-color'; value: string } | null {
  if (!value) return null;
  const cleaned = value.replace(/;$/, '');
  if (isGradient(cleaned)) {
    return { property: 'background-image', value: cleaned };
  }
  return { property: 'background-color', value: cleaned };
}

/**
 * Determines whether the background is dark, considering textColorOverride, custom background
 * colors (hex/gradient), and named background colors.
 */
export function resolveIsDarkBackground({
  backgroundColor,
  customBackgroundColor,
  textColorOverride,
}: {
  backgroundColor?: string | null;
  customBackgroundColor?: string | null;
  textColorOverride?: TextColorOverride;
}): boolean {
  if (textColorOverride === 'light') return true;
  if (textColorOverride === 'dark') return false;

  if (customBackgroundColor) {
    if (isGradient(customBackgroundColor)) return true;
    if (isValidHexColor(customBackgroundColor)) {
      return hexToRelativeLuminance(customBackgroundColor) < DARK_BACKGROUND_LUMINANCE_THRESHOLD;
    }
  }

  return isInvertedColor(backgroundColor ?? '');
}

/**
 * This is a rather dangerous function that parses and rotates linear gradients.
 *
 * In an ideal world we could apply the gradient to a pseudo element and then rotate it to avoid risky logic.
 * This is currently problematic because dimensions are maintained from before the rotation so a rectangle being rotated
 * becomes cropped. In the future we could use units like cqw/cqh to enforce dimensions on a rotated element and remove this function.
 *
 * https://developer.mozilla.org/en-US/docs/Web/CSS/Guides/Containment/Container_queries
 */
const LINEAR_GRADIENT_REGEX = /([-\d]+)+deg/;
const DIRECTION_GRADIENT_REGEX = /to (left|right|bottom|top)/;
const HAS_DEGREE_REGEX = /\d+deg/;
const DIRECTIONS_ARRAY = ['top', 'right', 'bottom', 'left', 'top'] as const;

export function rotateLinearGradient(gradient: string): string | undefined {
  try {
    const degreeMatch = gradient.match(LINEAR_GRADIENT_REGEX);
    if (degreeMatch?.[1]) {
      return gradient.replace(degreeMatch[1], (parseInt(degreeMatch[1], 10) + 90).toString());
    }

    const directionMatch = gradient.match(DIRECTION_GRADIENT_REGEX);
    if (directionMatch?.[1]) {
      const nextDirection = DIRECTIONS_ARRAY[DIRECTIONS_ARRAY.indexOf(directionMatch[1]) + 1];
      // This is safe because the REGEX capture values are hardcoded to the array values.
      return gradient.replace(directionMatch[1], nextDirection as string);
    }

    // Handle default case: no direction specified defaults to "to bottom", rotate to "to left"
    if (
      gradient.includes('linear-gradient(') &&
      !HAS_DEGREE_REGEX.test(gradient) &&
      !DIRECTION_GRADIENT_REGEX.test(gradient)
    ) {
      return gradient.replace('linear-gradient(', 'linear-gradient(to left, ');
    }

    return gradient;
  } catch (error) {
    Sentry.captureException(error, {
      extra: { message: `[Gradient Rotation] Utility function for rotating the gradient: {${gradient}}` },
    });
  }

  return undefined;
}
